import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  BookOpen,
  ChevronRight,
  ChevronLeft,
  Lightbulb,
  Target,
  MapPin,
  GraduationCap,
  TrendingUp,
  AlertCircle,
  Star,
  Search,
  X,
  Heart,
  Award,
  Loader2,
  Highlighter
} from 'lucide-react';
import { useCollegeNotebook } from '../../../hooks/useCollegeNotebook';
import NotesSectionV2 from './NotesSectionV2';
import HighlightPopup from './HighlightPopup';
import HighlightedInsights from './HighlightedInsights';
import CollegeResearchPanel from '../WhySchool/panels/CollegeResearchPanel';

/**
 * College Notebook - Sidebar reference panel for essay development
 *
 * Displays all strategic information about a college while working on essays.
 * Pulls data from college strategy and comparison modules.
 *
 * @param {string} collegeId - The college_journey_colleges ID
 * @param {string} essaySlotId - Optional essay slot ID for essay-specific context
 * @param {string} builderSection - Current Why School builder section (for showing relevant research)
 * @param {object} collegeData - College data with global_colleges merged in
 * @param {string} essaySubtype - Type of essay ('why_school' | 'why_major')
 */
export default function CollegeNotebook({ collegeId, essaySlotId = null, builderSection = null, collegeData = null, essaySubtype = 'why_school' }) {
  const {
    notebookData,
    loading,
    error,
    refresh,
    collegeNotes,
    essayNotes,
    saveCollegeNotes,
    saveEssayNotes,
    notesSaving,
    highlights,
    highlightsLoading,
    createHighlight,
    deleteHighlight,
    updateHighlightCategory
  } = useCollegeNotebook(collegeId, essaySlotId);

  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    research: true, // Start with research expanded (for Why School builder)
    notes: true, // Start with notes expanded
    highlights: true, // Start with highlights expanded
    prompt: true,
    insights: true,
    positioning: false,
    edge: false,
    values: false,
    valueProposition: false,
    advantages: false,
    address: false,
    academicFit: false,
    cultureFit: false,
    edgeAndFit: false
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Highlighting state
  const [showHighlightPopup, setShowHighlightPopup] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selection, setSelection] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [currentSection, setCurrentSection] = useState('');
  const contentRef = useRef(null);
  const sectionRefs = useRef({});

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle text selection for highlighting
  const handleTextSelection = useCallback((sectionName) => {
    const sel = window.getSelection();
    const text = sel?.toString().trim();

    if (text && text.length > 10) { // Minimum 10 characters
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      setSelectedText(text);
      setSelection(sel);
      setCurrentSection(sectionName);
      setPopupPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
      setShowHighlightPopup(true);
    }
  }, []);

  // Save highlight
  const handleSaveHighlight = useCallback(async (highlightData) => {
    try {
      await createHighlight({
        text: highlightData.text,
        category: highlightData.category,
        section: currentSection,
        context: null
      });

      // Clear selection and close popup
      window.getSelection()?.removeAllRanges();
      setShowHighlightPopup(false);
      setSelectedText('');
      setSelection(null);
    } catch (error) {
      console.error('Error saving highlight:', error);
      alert('Failed to save highlight');
    }
  }, [createHighlight, currentSection]);

  // Cancel highlight
  const handleCancelHighlight = useCallback(() => {
    window.getSelection()?.removeAllRanges();
    setShowHighlightPopup(false);
    setSelectedText('');
    setSelection(null);
  }, []);

  // Jump to section
  const handleJumpToSection = useCallback((sectionName) => {
    const sectionRef = sectionRefs.current[sectionName];
    if (sectionRef) {
      // Expand the section if collapsed
      if (!expandedSections[sectionName]) {
        toggleSection(sectionName);
      }

      // Scroll to section
      setTimeout(() => {
        sectionRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [expandedSections]);

  // Search across all notebook content
  const searchResults = useMemo(() => {
    if (!searchQuery.trim() || !notebookData) return null;

    const query = searchQuery.toLowerCase();
    const results = [];

    // Helper to check if text matches
    const matches = (text) => text && text.toLowerCase().includes(query);

    // Helper to add result
    const addResult = (section, content) => {
      results.push({ section, content });
    };

    // Search through all sections
    if (notebookData.essayPrompt && matches(notebookData.essayPrompt.prompt)) {
      addResult('prompt', notebookData.essayPrompt.prompt);
    }

    notebookData.insights?.forEach(insight => {
      if (matches(insight.content)) addResult('insights', insight.content);
    });

    notebookData.positioning?.forEach(item => {
      if (matches(item)) addResult('positioning', item);
    });

    notebookData.strategicEdge?.forEach(item => {
      if (matches(item)) addResult('edge', item);
    });

    notebookData.advantages?.forEach(item => {
      if (matches(item)) addResult('advantages', item);
    });

    notebookData.aspectsToAddress?.forEach(item => {
      if (matches(item)) addResult('address', item);
    });

    if (notebookData.valueProposition && matches(notebookData.valueProposition.text)) {
      addResult('valueProposition', notebookData.valueProposition.text);
    }

    return results;
  }, [searchQuery, notebookData]);

  // Don't render if no college ID provided
  if (!collegeId) return null;

  const getColorClasses = (color) => {
    const colors = {
      teal: 'border-teal-500 bg-teal-50',
      indigo: 'border-indigo-500 bg-indigo-50',
      azure: 'border-azure-500 bg-azure-50',
      coral: 'border-coral-500 bg-coral-50',
      gold: 'border-gold-500 bg-gold-50',
      emerald: 'border-emerald-500 bg-emerald-50'
    };
    return colors[color] || colors.teal;
  };

  const getIconComponent = (iconName) => {
    const icons = {
      lightbulb: Lightbulb,
      target: Target,
      location: MapPin,
      graduation: GraduationCap,
      heart: Heart,
      award: Award
    };
    const Icon = icons[iconName] || Lightbulb;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="fixed right-0 top-0 h-screen flex items-stretch z-50">
      {/* Toggle Tab */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-azure-600 text-white w-8 py-6 rounded-l-lg border-2 border-r-0 border-azure-600 hover:bg-azure-700 hover:w-10 transition-all flex flex-col items-center justify-center gap-2 group"
          title="Open College Notebook"
        >
          <BookOpen className="w-4 h-4" />
          <ChevronLeft className="w-3 h-3 opacity-50 group-hover:opacity-100" />
        </button>
      )}

      {/* Notebook Panel */}
      {isOpen && (
        <div className="bg-white w-96 h-full border-l-2 border-slate-900 flex flex-col shadow-xl">
          {/* Header */}
          <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between border-b-2 border-slate-900">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <BookOpen className="w-5 h-5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-bold">College Notebook</div>
                {notebookData && (
                  <div className="text-xs text-slate-300 truncate">
                    {notebookData.college}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-slate-800 rounded transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="p-3 border-b border-stone-200">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-500" />
              <input
                type="text"
                placeholder="Search notebook..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Search Results Preview */}
            {searchResults && searchResults.length > 0 && (
              <div className="mt-2 text-xs text-stone-600">
                Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-slate-600" />
              </div>
            )}

            {error && (
              <div className="p-4 text-center">
                <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm text-red-700">Failed to load notebook</p>
                <button
                  onClick={refresh}
                  className="mt-2 text-xs text-teal-600 hover:text-teal-700"
                >
                  Try again
                </button>
              </div>
            )}

            {notebookData && (
              <>
                {/* Research Section - Shows section-specific college data from Why School builder */}
                {builderSection && collegeData && (
                  <NotebookSection
                    title="College Research"
                    icon={<Target className="w-4 h-4 text-slate-700" />}
                    color="indigo"
                    expanded={expandedSections.research}
                    onToggle={() => toggleSection('research')}
                  >
                    <div className="text-xs text-slate-600">
                      <CollegeResearchPanel
                        collegeData={collegeData}
                        currentSection={builderSection}
                        essaySubtype={essaySubtype}
                      />
                    </div>
                  </NotebookSection>
                )}

                {/* Your Notes Section */}
                <NotebookSection
                  title="Your Notes"
                  icon={<Award className="w-4 h-4 text-slate-700" />}
                  color="emerald"
                  expanded={expandedSections.notes}
                  onToggle={() => toggleSection('notes')}
                >
                  <NotesSectionV2
                    collegeNotes={collegeNotes}
                    essayNotes={essayNotes}
                    onSaveCollegeNotes={saveCollegeNotes}
                    onSaveEssayNotes={saveEssayNotes}
                    saving={notesSaving}
                    hasEssaySlot={!!essaySlotId}
                  />
                </NotebookSection>

                {/* Highlighted Insights Section */}
                <NotebookSection
                  title="Highlighted Insights"
                  icon={<Highlighter className="w-4 h-4 text-slate-700" />}
                  color="gold"
                  expanded={expandedSections.highlights}
                  onToggle={() => toggleSection('highlights')}
                >
                  <HighlightedInsights
                    highlights={highlights}
                    loading={highlightsLoading}
                    onDelete={deleteHighlight}
                    onUpdateCategory={updateHighlightCategory}
                    onJumpToSection={handleJumpToSection}
                    collegeName={notebookData?.college || ''}
                  />
                </NotebookSection>

                {/* Essay Prompt (if applicable) */}
                {notebookData.essayPrompt && (
                  <NotebookSection
                    title="Essay Prompt"
                    icon={<GraduationCap className="w-4 h-4 text-slate-700" />}
                    color="indigo"
                    expanded={expandedSections.prompt}
                    onToggle={() => toggleSection('prompt')}
                    highlight={searchQuery && notebookData.essayPrompt.prompt.toLowerCase().includes(searchQuery.toLowerCase())}
                    sectionRef={(el) => (sectionRefs.current['prompt'] = el)}
                    onTextSelection={handleTextSelection}
                    sectionName="prompt"
                  >
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-2">
                      <p className="text-sm text-slate-800 leading-relaxed italic">
                        "{notebookData.essayPrompt.prompt}"
                      </p>
                      {notebookData.essayPrompt.wordLimit && (
                        <p className="text-xs text-slate-600 mt-2">
                          Word limit: {notebookData.essayPrompt.wordLimit}
                        </p>
                      )}
                    </div>
                  </NotebookSection>
                )}

                {/* Intelligent Insights */}
                {notebookData.insights && notebookData.insights.length > 0 && (
                  <NotebookSection
                    title="Intelligent Insights"
                    icon={<Lightbulb className="w-4 h-4 text-slate-700" />}
                    color="teal"
                    expanded={expandedSections.insights}
                    onToggle={() => toggleSection('insights')}
                    sectionRef={(el) => (sectionRefs.current['insights'] = el)}
                    onTextSelection={handleTextSelection}
                    sectionName="insights"
                  >
                    <div className="space-y-3">
                      {notebookData.insights.map((insight, idx) => (
                        <div key={idx} className={`border-l-4 ${getColorClasses(insight.color)} rounded-lg p-3`}>
                          <div className="flex items-center gap-2 mb-2">
                            {getIconComponent(insight.icon)}
                            <span className="text-xs font-bold text-slate-900 uppercase tracking-wide">
                              {insight.title}
                            </span>
                          </div>
                          <p className="text-xs text-slate-700 leading-relaxed">
                            {insight.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  </NotebookSection>
                )}

                {/* Specific Positioning */}
                {notebookData.positioning && notebookData.positioning.length > 0 && (
                  <NotebookSection
                    title="Specific Positioning"
                    icon={<Target className="w-4 h-4 text-slate-700" />}
                    color="indigo"
                    expanded={expandedSections.positioning}
                    onToggle={() => toggleSection('positioning')}
                    sectionRef={(el) => (sectionRefs.current['positioning'] = el)}
                    onTextSelection={handleTextSelection}
                    sectionName="positioning"
                  >
                    <BulletList items={notebookData.positioning} color="indigo" />
                  </NotebookSection>
                )}

                {/* Your Strategic Edge */}
                {notebookData.strategicEdge && notebookData.strategicEdge.length > 0 && (
                  <NotebookSection
                    title="Your Strategic Edge"
                    icon={<Star className="w-4 h-4 text-slate-700" />}
                    color="gold"
                    expanded={expandedSections.edge}
                    onToggle={() => toggleSection('edge')}
                  >
                    <BulletList items={notebookData.strategicEdge} color="gold" />
                  </NotebookSection>
                )}

                {/* What This School Values */}
                {notebookData.schoolValues && notebookData.schoolValues.length > 0 && (
                  <NotebookSection
                    title="What This School Values"
                    icon={<Heart className="w-4 h-4 text-slate-700" />}
                    color="coral"
                    expanded={expandedSections.values}
                    onToggle={() => toggleSection('values')}
                  >
                    <BulletList items={notebookData.schoolValues} color="coral" />
                  </NotebookSection>
                )}

                {/* Your Value Proposition */}
                {notebookData.valueProposition && (
                  <NotebookSection
                    title="Your Value Proposition"
                    icon={<Award className="w-4 h-4 text-slate-700" />}
                    color="emerald"
                    expanded={expandedSections.valueProposition}
                    onToggle={() => toggleSection('valueProposition')}
                  >
                    <p className="text-xs text-slate-700 leading-relaxed">
                      {notebookData.valueProposition.text || notebookData.valueProposition}
                    </p>
                  </NotebookSection>
                )}

                {/* Your Advantages */}
                {notebookData.advantages && notebookData.advantages.length > 0 && (
                  <NotebookSection
                    title="Your Advantages"
                    icon={<Star className="w-4 h-4 text-slate-700" />}
                    color="teal"
                    expanded={expandedSections.advantages}
                    onToggle={() => toggleSection('advantages')}
                    sectionRef={(el) => (sectionRefs.current['advantages'] = el)}
                    onTextSelection={handleTextSelection}
                    sectionName="advantages"
                  >
                    <BulletList items={notebookData.advantages} color="teal" />
                  </NotebookSection>
                )}

                {/* Aspects to Address */}
                {notebookData.aspectsToAddress && notebookData.aspectsToAddress.length > 0 && (
                  <NotebookSection
                    title="Aspects to Address"
                    icon={<AlertCircle className="w-4 h-4 text-slate-700" />}
                    color="gold"
                    expanded={expandedSections.address}
                    onToggle={() => toggleSection('address')}
                  >
                    <BulletList items={notebookData.aspectsToAddress} color="gold" />
                  </NotebookSection>
                )}

                {/* Academic Fit Analysis */}
                {notebookData.academicFit && (
                  <NotebookSection
                    title="Academic Fit Analysis"
                    icon={<GraduationCap className="w-4 h-4 text-slate-700" />}
                    color="indigo"
                    expanded={expandedSections.academicFit}
                    onToggle={() => toggleSection('academicFit')}
                  >
                    {notebookData.academicFit.summary && (
                      <p className="text-xs text-slate-700 leading-relaxed mb-3">
                        {notebookData.academicFit.summary}
                      </p>
                    )}
                    {notebookData.academicFit.strengths && notebookData.academicFit.strengths.length > 0 && (
                      <>
                        <div className="text-xs font-bold text-slate-900 mb-1 mt-3">Strengths:</div>
                        <BulletList items={notebookData.academicFit.strengths} color="teal" />
                      </>
                    )}
                    {notebookData.academicFit.academicCultureMatch && notebookData.academicFit.academicCultureMatch.length > 0 && (
                      <>
                        <div className="text-xs font-bold text-slate-900 mb-1 mt-3">Academic Culture Match:</div>
                        <BulletList items={notebookData.academicFit.academicCultureMatch} color="indigo" />
                      </>
                    )}
                    {notebookData.academicFit.considerations && notebookData.academicFit.considerations.length > 0 && (
                      <>
                        <div className="text-xs font-bold text-slate-900 mb-1 mt-3">Considerations:</div>
                        <BulletList items={notebookData.academicFit.considerations} color="gold" />
                      </>
                    )}
                  </NotebookSection>
                )}

                {/* Culture Fit Analysis */}
                {notebookData.cultureFit && (
                  <NotebookSection
                    title="Culture Fit Analysis"
                    icon={<Heart className="w-4 h-4 text-slate-700" />}
                    color="coral"
                    expanded={expandedSections.cultureFit}
                    onToggle={() => toggleSection('cultureFit')}
                  >
                    {notebookData.cultureFit.summary && (
                      <p className="text-xs text-slate-700 leading-relaxed mb-3">
                        {notebookData.cultureFit.summary}
                      </p>
                    )}
                    {notebookData.cultureFit.alignment && notebookData.cultureFit.alignment.length > 0 && (
                      <>
                        <div className="text-xs font-bold text-slate-900 mb-1 mt-3">Campus Culture & Alignment:</div>
                        <BulletList items={notebookData.cultureFit.alignment} color="coral" />
                      </>
                    )}
                    {notebookData.cultureFit.socialFit && notebookData.cultureFit.socialFit.length > 0 && (
                      <>
                        <div className="text-xs font-bold text-slate-900 mb-1 mt-3">Social & Extracurricular Fit:</div>
                        <BulletList items={notebookData.cultureFit.socialFit} color="teal" />
                      </>
                    )}
                    {notebookData.cultureFit.potential_challenges && notebookData.cultureFit.potential_challenges.length > 0 && (
                      <>
                        <div className="text-xs font-bold text-slate-900 mb-1 mt-3">Considerations:</div>
                        <BulletList items={notebookData.cultureFit.potential_challenges} color="gold" />
                      </>
                    )}
                  </NotebookSection>
                )}

                {/* Your Edge and Fit */}
                {notebookData.edgeAndFit && (
                  notebookData.edgeAndFit.yourEdge?.length > 0 ||
                  notebookData.edgeAndFit.hiddenAdvantage?.length > 0 ||
                  notebookData.edgeAndFit.whyThisFits?.length > 0
                ) && (
                  <NotebookSection
                    title="Your Edge and Fit"
                    icon={<Star className="w-4 h-4 text-slate-700" />}
                    color="gold"
                    expanded={expandedSections.edgeAndFit}
                    onToggle={() => toggleSection('edgeAndFit')}
                  >
                    {notebookData.edgeAndFit.yourEdge?.length > 0 && (
                      <>
                        <div className="text-xs font-bold text-slate-900 mb-1">Your Edge:</div>
                        <BulletList items={notebookData.edgeAndFit.yourEdge} color="gold" />
                      </>
                    )}
                    {notebookData.edgeAndFit.hiddenAdvantage?.length > 0 && (
                      <>
                        <div className="text-xs font-bold text-slate-900 mb-1 mt-3">Hidden Advantages:</div>
                        <BulletList items={notebookData.edgeAndFit.hiddenAdvantage} color="emerald" />
                      </>
                    )}
                  </NotebookSection>
                )}

                {/* Strategic Research Bank */}
                {notebookData.researchBank && (
                  notebookData.researchBank.programs?.length > 0 ||
                  notebookData.researchBank.culture?.length > 0
                ) && (
                  <NotebookSection
                    title="Strategic Research Bank"
                    icon={<TrendingUp className="w-4 h-4 text-slate-700" />}
                    color="azure"
                    expanded={expandedSections.research}
                    onToggle={() => toggleSection('research')}
                    sectionRef={(el) => (sectionRefs.current['research'] = el)}
                    onTextSelection={handleTextSelection}
                    sectionName="research"
                  >
                    {notebookData.researchBank.programs?.length > 0 && (
                      <div className="mb-4">
                        <div className="text-xs font-bold text-slate-900 mb-2 uppercase tracking-wide">
                          Specific Programs
                        </div>
                        {notebookData.researchBank.programs.map((program, idx) => (
                          <div key={idx} className="mb-3 pb-3 border-b border-stone-200 last:border-0">
                            <div className="text-xs font-bold text-slate-900 mb-1">
                              {program.name}
                            </div>
                            {program.details && program.details.length > 0 && (
                              <div className="space-y-1">
                                {program.details.map((detail, detailIdx) => (
                                  <p key={detailIdx} className="text-xs text-slate-700 leading-relaxed pl-2">
                                    • {detail}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {notebookData.researchBank.culture?.length > 0 && (
                      <div>
                        <div className="text-xs font-bold text-slate-900 mb-2 uppercase tracking-wide">
                          Cultural Insights
                        </div>
                        <BulletList items={notebookData.researchBank.culture} color="azure" />
                      </div>
                    )}
                  </NotebookSection>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-stone-200 px-4 py-2 bg-stone-50">
            <p className="text-xs text-stone-600 text-center">
              Reference these insights while writing • Select text to highlight
            </p>
          </div>
        </div>
      )}

      {/* Highlight Popup */}
      {showHighlightPopup && (
        <HighlightPopup
          selectedText={selectedText}
          selection={selection}
          onSave={handleSaveHighlight}
          onCancel={handleCancelHighlight}
          position={popupPosition}
        />
      )}
    </div>
  );
}

// Reusable section component with ref forwarding and text selection support
function NotebookSection({
  title,
  icon,
  color,
  expanded,
  onToggle,
  children,
  highlight = false,
  sectionRef = null,
  onTextSelection = null,
  sectionName = ''
}) {
  const colorDot = {
    teal: 'bg-teal-500',
    indigo: 'bg-indigo-500',
    azure: 'bg-azure-500',
    coral: 'bg-coral-500',
    gold: 'bg-gold-500',
    emerald: 'bg-emerald-500'
  }[color] || 'bg-teal-500';

  const handleMouseUp = () => {
    if (onTextSelection && sectionName) {
      setTimeout(() => {
        onTextSelection(sectionName);
      }, 10);
    }
  };

  return (
    <div
      ref={sectionRef}
      className={`border-b border-stone-200 ${highlight ? 'bg-yellow-50' : ''}`}
      onMouseUp={handleMouseUp}
    >
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-stone-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${colorDot}`}></div>
          {icon}
          <span className="text-sm font-bold text-slate-900">{title}</span>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-slate-500 transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          {children}
        </div>
      )}
    </div>
  );
}

// Reusable bullet list component
function BulletList({ items, color }) {
  const bulletColor = {
    teal: 'bg-teal-500',
    indigo: 'bg-indigo-500',
    azure: 'bg-azure-500',
    coral: 'bg-coral-500',
    gold: 'bg-gold-500',
    emerald: 'bg-emerald-500'
  }[color] || 'bg-teal-500';

  return (
    <div className="space-y-2">
      {items.map((item, idx) => {
        // Handle both string items and object items
        let content = item;
        if (typeof item === 'object' && item !== null) {
          // If it's an object, try to extract meaningful text
          content = item.text || item.content || item.description || item.title || JSON.stringify(item);
        }

        return (
          <div key={idx} className="flex gap-2">
            <div className={`w-1 h-1 rounded-full ${bulletColor} mt-1.5 flex-shrink-0`}></div>
            <p className="text-xs text-slate-700 leading-relaxed">{content}</p>
          </div>
        );
      })}
    </div>
  );
}
